/**
 *      ▄ █
 *    █ █ █▄▄  LOADBALANCER CLIENT
 *    █ █ ▄ █
 *    ▀     ▀  @copyright Copyright (c) 2025
 */
import { AclModel, L4BackendServiceModel, L4FrontendServiceModel, L4RealServiceModel } from '../models';
import { ApiService, AuthStoreService } from '../../../../core/services';
import { ErrorResponseModel } from '../../../../core/models';
import { Injectable } from '@angular/core';
import { L4AclsEnum, L4BackendsEnum, L4FrontendsEnum, L4RealServersEnum } from '../enums';
import { ModulesEnum } from '../../../../core/enums';
import { Observable, Subscriber } from 'rxjs';

/**
 * Network Service
 * Note: This class is to be used to make API calls
 *       and return with a structured response.
 */
@Injectable()
export class Layer4ApiService {
    constructor(
        private apiService: ApiService,
        private authStoreService: AuthStoreService,
    ) {
    }

    /**
     * API call to get all l7 servicesFrontend
     * @returns {Observable}
     */
    public getFrontends(options: any = {}): Observable<L4FrontendServiceModel[]> {
        return new Observable((observer: Subscriber<any>): void => {
                // Get our selected module details
                const moduleAuth: any = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

                this.apiService.get(L4FrontendsEnum.GET_LAYER_4_FRONTENDS, moduleAuth, false, options)
                    .subscribe({
                        next: (response: L4FrontendServiceModel[]): void => {
                            if (response) {
                                observer.next(response);
                                observer.complete();
                            }
                            observer.error('API response does not contain the required data');
                            observer.complete();
                        },
                        error: (error: ErrorResponseModel): void => {
                            observer.error(error);
                            observer.complete();
                        }
                    });
            }
        );
    }

    /**
     * API call to get a specific l7 frontend
     * @param id
     * @param options
     */
    public getFrontend(id: string, options?: any): Observable<L4FrontendServiceModel> {
        return new Observable((observer: Subscriber<any>): void => {
                // Get our selected module details
                const moduleAuth: any = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

                this.apiService.get(L4FrontendsEnum.GET_LAYER_4_FRONTEND(id), moduleAuth)
                    .subscribe({
                        next: (response: L4FrontendServiceModel): void => {
                            if (response) {
                                observer.next(response);
                                observer.complete();
                            }
                            observer.error('API response does not contain the required data');
                            observer.complete();
                        },
                        error: (error: ErrorResponseModel): void => {
                            observer.error(error);
                            observer.complete();
                        }
                    });
            }
        );
    }

    /**
     * API call to add a l7 frontend
     * @param service
     * @param options
     */
    public addFrontend(service: L4FrontendServiceModel, options?: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
                // Get our selected module details
                const moduleAuth: any = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

                this.apiService.post(L4FrontendsEnum.POST_LAYER_4_FRONTEND, service, moduleAuth)
                    .subscribe({
                        next: (data: L4FrontendServiceModel): void => {
                            const response = data;
                            // Add additional properties
                            response.name = service.name;
                            // observe next value
                            observer.next({ vip: response });
                            observer.complete();
                        },
                        error: (error: ErrorResponseModel): void => {
                            observer.error(error);
                            observer.complete();
                        }
                    });
            }
        );
    }

    /**
     * API call to delete a l7 frontend
     * @param id
     * @param options
     */
    public deleteFrontend(id: string, options?: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
                // Get our selected module details
                const moduleAuth: any = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

                this.apiService.delete(L4FrontendsEnum.DELETE_LAYER_4_FRONTEND(id), moduleAuth)
                    .subscribe({
                        next: (response: any): void => {
                            observer.next(response);
                            observer.complete();
                        },
                        error: (error: ErrorResponseModel): void => {
                            observer.error(error);
                            observer.complete();
                        }
                    });
            }
        );
    }

    /**
     * API call to update a l7 frontend
     * @param id
     * @param service
     * @param options
     */
    public updateFrontend(id: string, service: L4FrontendServiceModel, options?: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
                // Get our selected module details
                const moduleAuth: any = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

                this.apiService.put(L4FrontendsEnum.PUT_LAYER_4_FRONTEND(id), service, moduleAuth)
                    .subscribe({
                        next: (response: any): void => {
                            observer.next(response);
                            observer.complete();
                        },
                        error: (error: ErrorResponseModel): void => {
                            observer.error(error);
                            observer.complete();
                        }
                    });
            }
        );
    }

    /**
     * Drain a frontend by ID.
     * @param id
     * @param options
     */
    public drainFrontend(id: string, options?: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
                // Get our selected module details
                const moduleAuth: any = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

                this.apiService.put(L4FrontendsEnum.PUT_DRAIN_LAYER_4_FRONTEND(id), {}, moduleAuth)
                    .subscribe({
                        next: (response: any): void => {
                            observer.next(response);
                            observer.complete();
                        },
                        error: (error: ErrorResponseModel): void => {
                            observer.error(error);
                            observer.complete();
                        }
                    });
            }
        );
    }

    /**
     * Online a frontend by ID.
     * @param id
     * @param options
     */
    public onlineFrontend(id: string, options?: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
                // Get our selected module details
                const moduleAuth: any = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

                this.apiService.put(L4FrontendsEnum.PUT_ONLINE_LAYER_4_FRONTEND(id), {}, moduleAuth)
                    .subscribe({
                        next: (response: any): void => {
                            observer.next(response);
                            observer.complete();
                        },
                        error: (error: ErrorResponseModel): void => {
                            observer.error(error);
                            observer.complete();
                        }
                    });
            }
        );
    }

    /**
     * Halt a frontend by ID.
     * @param id
     * @param options
     */
    public haltFrontend(id: string, options?: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
                // Get our selected module details
                const moduleAuth: any = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

                this.apiService.put(L4FrontendsEnum.PUT_HALT_LAYER_4_FRONTEND(id), {}, moduleAuth)
                    .subscribe({
                        next: (response: any): void => {
                            observer.next(response);
                            observer.complete();
                        },
                        error: (error: ErrorResponseModel): void => {
                            observer.error(error);
                            observer.complete();
                        }
                    });
            }
        );
    }

    /**
     * Get a specific ACL rule via the api
     * @param frontendId
     * @param options
     */
    public getFrontendAcls(frontendId: string, options?: any): Observable<AclModel[]> {
        return new Observable((observer: Subscriber<any>): void => {
                // Get our selected module details
                const moduleAuth: any = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

                this.apiService.get(L4FrontendsEnum.GET_HAPROXY_LAYER_4_ACLS(frontendId), moduleAuth)
                    .subscribe({
                        next: (response: AclModel[]): void => {
                            observer.next(response);
                            observer.complete();
                        },
                        error: (error: ErrorResponseModel): void => {
                            observer.error(error);
                            observer.complete();
                        }
                    });
            }
        );
    }

    /**
     * API call to get all the haproxy backends
     * @param options
     */
    public getBackends(options: any = {}): Observable<L4BackendServiceModel[]> {
        return new Observable((observer: Subscriber<any>): void => {
                // Get our selected module details
                const moduleAuth: any = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

                this.apiService.get(L4BackendsEnum.GET_LAYER_4_BACKENDS, moduleAuth, false, options)
                    .subscribe({
                        next: (response: L4BackendServiceModel[]): void => {
                            if (response) {
                                observer.next(response);
                                observer.complete();
                            }
                            observer.error('API response does not contain the required data');
                            observer.complete();
                        },
                        error: (error: ErrorResponseModel): void => {
                            observer.error(error);
                            observer.complete();
                        }
                    });
            }
        );
    }

    /**
     * Get a specific backend via the api
     * @param id
     * @param options
     */
    public getBackend(id: string, options?: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
                // Get our selected module details
                const moduleAuth: any = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

                this.apiService.get(L4BackendsEnum.GET_LAYER_4_BACKEND(id), moduleAuth)
                    .subscribe({
                        next: (response: L4BackendServiceModel): void => {
                            if (response) {
                                observer.next(response);
                                observer.complete();
                            }
                            observer.error('API response does not contain the required data');
                            observer.complete();
                        },
                        error: (error: ErrorResponseModel): void => {
                            observer.error(error);
                            observer.complete();
                        }
                    });
            }
        );
    }

    /**
     * Get rips by backend id via the api
     * @param id
     * @param options
     */
    public getBackendsByFrontendId(id: string, options?: any): Observable<L4BackendServiceModel[]> {
        return new Observable((observer: Subscriber<any>): void => {
                // Get our selected module details
                const moduleAuth: any = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

                this.apiService.get(L4BackendsEnum.GET_LAYER_4_BACKENDS_BY_FRONTEND_ID(id), moduleAuth)
                    .subscribe({
                        next: (response: L4BackendServiceModel[]): void => {
                            if (response) {
                                observer.next(response);
                                observer.complete();
                            }
                            observer.error('API response does not contain the required data');
                            observer.complete();
                        },
                        error: (error: ErrorResponseModel): void => {
                            observer.error(error);
                            observer.complete();
                        }
                    });
            }
        );
    }

    /**
     * Add a new backend via the api
     * @param backend
     * @param options
     */
    public addBackend(backend: L4BackendServiceModel, options?: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
                // Get our selected module details
                const moduleAuth: any = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

                this.apiService.post(L4BackendsEnum.POST_LAYER_4_BACKEND, backend, moduleAuth)
                    .subscribe({
                        next: (data: any): void => {
                            const response = data;
                            // Add additional properties
                            response.name = backend.name;
                            // observe next value
                            observer.next({ backend: response });
                            observer.complete();
                        },
                        error: (error: ErrorResponseModel): void => {
                            observer.error(error);
                            observer.complete();
                        }
                    });
            }
        );
    }

    /**
     * Delete a backend by ID.
     * @param id
     * @param options
     */
    public deleteBackend(id: string, options?: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
                // Get our selected module details
                const moduleAuth: any = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

                this.apiService.delete(L4BackendsEnum.DELETE_LAYER_4_BACKEND(id), moduleAuth)
                    .subscribe({
                        next: (response: any): void => {
                            observer.next(response);
                            observer.complete();
                        },
                        error: (error: ErrorResponseModel): void => {
                            observer.error(error);
                            observer.complete();
                        }
                    });
            }
        );
    }

    /**
     * Drain a backend by ID.
     * @param id
     * @param options
     */
    public drainBackend(id: string, options?: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
                // Get our selected module details
                const moduleAuth: any = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

                this.apiService.put(L4BackendsEnum.PUT_DRAIN_LAYER_4_BACKEND(id), {}, moduleAuth)
                    .subscribe({
                        next: (response: any): void => {
                            observer.next(response);
                            observer.complete();
                        },
                        error: (error: ErrorResponseModel): void => {
                            observer.error(error);
                            observer.complete();
                        }
                    });
            }
        );
    }

    /**
     * Online a backend by ID.
     * @param id
     * @param options
     */
    public onlineBackend(id: string, options?: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
                // Get our selected module details
                const moduleAuth: any = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

                this.apiService.put(L4BackendsEnum.PUT_ONLINE_LAYER_4_BACKEND(id), {}, moduleAuth)
                    .subscribe({
                        next: (response: any): void => {
                            observer.next(response);
                            observer.complete();
                        },
                        error: (error: ErrorResponseModel): void => {
                            observer.error(error);
                            observer.complete();
                        }
                    });
            }
        );
    }

    /**
     * Halt a backend by ID (this will halt all real servers linked to this backend).
     * @param id
     * @param options
     */
    public haltBackend(id: string, options?: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
                // Get our selected module details
                const moduleAuth: any = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

                this.apiService.put(L4BackendsEnum.PUT_HALT_LAYER_4_BACKEND(id), {}, moduleAuth)
                    .subscribe({
                        next: (response: any): void => {
                            observer.next(response);
                            observer.complete();
                        },
                        error: (error: ErrorResponseModel): void => {
                            observer.error(error);
                            observer.complete();
                        }
                    });
            }
        );
    }

    /**
     * Update a backend via the api
     * @param id
     * @param data - either a backend object or a property and values "ripIds": ["{{real-2-id}}", "{{real-3-id}}"]
     * @param options
     */
    public updateBackend(id: string, data: L4BackendServiceModel | any, options?: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
                // Get our selected module details
                const moduleAuth: any = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

                this.apiService.put(L4BackendsEnum.PUT_LAYER_4_BACKEND(id), data, moduleAuth)
                    .subscribe({
                        next: (response: any): void => {
                            observer.next(response);
                            observer.complete();
                        },
                        error: (error: ErrorResponseModel): void => {
                            observer.error(error);
                            observer.complete();
                        }
                    });
            }
        );
    }

    /**
     * Get all rips via the api
     * @param options
     */
    public getRealServers(options: any = {}): Observable<L4RealServiceModel[]> {
        return new Observable((observer: Subscriber<any>): void => {
                // Get our selected module details
                const moduleAuth: any = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

                this.apiService.get(L4RealServersEnum.GET_LAYER_4_RIPS, moduleAuth, false, options)
                    .subscribe({
                        next: (response: L4RealServiceModel[]): void => {
                            observer.next(response);
                            observer.complete();
                        },
                        error: (error: ErrorResponseModel): void => {
                            observer.error(error);
                            observer.complete();
                        }
                    });
            }
        );
    }

    /**
     * Get rips by id via the api
     * @param id
     * @param options
     */
    public getRealServer(id: string, options?: any): Observable<L4RealServiceModel> {
        return new Observable((observer: Subscriber<any>): void => {
                // Get our selected module details
                const moduleAuth: any = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

                this.apiService.get(L4RealServersEnum.GET_LAYER_4_RIP(id), moduleAuth)
                    .subscribe({
                        next: (response: L4RealServiceModel): void => {
                            if (response) {
                                observer.next(response);
                                observer.complete();
                            }
                            observer.error('API response does not contain the required data');
                            observer.complete();
                        },
                        error: (error: ErrorResponseModel): void => {
                            observer.error(error);
                            observer.complete();
                        }
                    });
            }
        );
    }

    /**
     * Get rips by backend id via the api
     * @param id
     * @param options
     */
    public getRealServersByBackendId(id: string, options?: any): Observable<L4RealServiceModel[]> {
        return new Observable((observer: Subscriber<any>): void => {
                // Get our selected module details
                const moduleAuth: any = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

                this.apiService.get(L4RealServersEnum.GET_LAYER_4_RIPS_BY_BACKEND_ID(id), moduleAuth)
                    .subscribe({
                        next: (response: L4RealServiceModel[]): void => {
                            if (response) {
                                observer.next(response);
                                observer.complete();
                            }
                            observer.error('API response does not contain the required data');
                            observer.complete();
                        },
                        error: (error: ErrorResponseModel): void => {
                            observer.error(error);
                            observer.complete();
                        }
                    });
            }
        );
    }

    /**
     * Add a rip via the api
     * @param rip
     * @param options
     */
    public addRealServer(rip: L4RealServiceModel, options?: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
                // Get our selected module details
                const moduleAuth: any = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

                this.apiService.post(L4RealServersEnum.POST_LAYER_4_RIPS, rip, moduleAuth)
                    .subscribe({
                        next: (response: L4RealServiceModel): void => {
                            observer.next({ rip: response });
                            observer.complete();
                        },
                        error: (error: ErrorResponseModel): void => {
                            observer.error(error);
                            observer.complete();
                        }
                    });
            }
        );
    }

    /**
     *  Delete a RIP via the api
     * @param id
     * @param options
     */
    public deleteRealServer(id: string, options?: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
                // Get our selected module details
                const moduleAuth: any = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

                this.apiService.delete(L4RealServersEnum.DELETE_LAYER_4_RIPS(id), moduleAuth)
                    .subscribe({
                        next: (response: any): void => {
                            observer.next(response);
                            observer.complete();
                        },
                        error: (error: ErrorResponseModel): void => {
                            observer.error(error);
                            observer.complete();
                        }
                    });
            }
        );
    }

    /**
     * Drain a real server by ID.
     * @param id
     * @param options
     */
    public drainRealServer(id: string, options?: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
                // Get our selected module details
                const moduleAuth: any = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

                this.apiService.put(L4RealServersEnum.PUT_DRAIN_LAYER_4_RIP(id), {}, moduleAuth)
                    .subscribe({
                        next: (response: any): void => {
                            observer.next(response);
                            observer.complete();
                        },
                        error: (error: ErrorResponseModel): void => {
                            observer.error(error);
                            observer.complete();
                        }
                    });
            }
        );
    }

    /**
     * Online a real server by ID.
     * @param id
     * @param options
     */
    public onlineRealServer(id: string, options?: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
                // Get our selected module details
                const moduleAuth: any = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

                this.apiService.put(L4RealServersEnum.PUT_ONLINE_LAYER_4_RIP(id), {}, moduleAuth)
                    .subscribe({
                        next: (response: any): void => {
                            observer.next(response);
                            observer.complete();
                        },
                        error: (error: ErrorResponseModel): void => {
                            observer.error(error);
                            observer.complete();
                        }
                    });
            }
        );
    }

    /**
     * Online a real server by ID.
     * @param id
     * @param options
     */
    public haltRealServer(id: string, options?: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
                // Get our selected module details
                const moduleAuth: any = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

                this.apiService.put(L4RealServersEnum.PUT_HALT_LAYER_4_RIP(id), {}, moduleAuth)
                    .subscribe({
                        next: (response: any): void => {
                            observer.next(response);
                            observer.complete();
                        },
                        error: (error: ErrorResponseModel): void => {
                            observer.error(error);
                            observer.complete();
                        }
                    });
            }
        );
    }

    /**
     * Update a rip via the api service
     * @param id
     * @param rip
     * @param options
     */
    public updateRealServer(id: string, rip: L4RealServiceModel, options?: any) {
        return new Observable((observer: Subscriber<any>): void => {
                // Get our selected module details
                const moduleAuth: any = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

                this.apiService.put(L4RealServersEnum.PUT_LAYER_4_RIPS(id), rip, moduleAuth)
                    .subscribe({
                        next: (response: any): void => {
                            observer.next(response);
                            observer.complete();
                        },
                        error: (error: ErrorResponseModel): void => {
                            observer.error(error);
                            observer.complete();
                        }
                    });
            }
        );
    }

    /**
     * Get hanging realservers.
     * @param options
     */
    public getHangingRealServers(options?: any): Observable<L4RealServiceModel> {
        return new Observable((observer: Subscriber<any>): void => {
                // Get our selected module details
                const moduleAuth: any = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

                this.apiService.get(L4RealServersEnum.GET_HANGING_LAYER_4_RIPS, moduleAuth)
                    .subscribe({
                        next: (response: L4RealServiceModel): void => {
                            if (response) {
                                observer.next(response);
                                observer.complete();
                            }
                            observer.error('API response does not contain the required data');
                            observer.complete();
                        },
                        error: (error: ErrorResponseModel): void => {
                            observer.error(error);
                            observer.complete();
                        }
                    });
            }
        );
    }

    /**
     * Get all the acl rules via the api
     * @param options
     */
    public getAclRules(options: any = {}): Observable<AclModel[]> {
        return new Observable((observer: Subscriber<any>): void => {
                // Get our selected module details
                const moduleAuth: any = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

                this.apiService.get(L4AclsEnum.GET_HAPROXY_ACLS, moduleAuth, false, options)
                    .subscribe({
                        next: (response: AclModel[]): void => {
                            if (response) {
                                observer.next(response);
                                observer.complete();
                            }
                            observer.error('API response does not contain the required data');
                            observer.complete();
                        },
                        error: (error: ErrorResponseModel): void => {
                            observer.error(error);
                            observer.complete();
                        }
                    });
            }
        );
    }

    /**
     * Add a new acl rule via the API
     * @param rule
     * @param options
     */
    public addAclRule(rule: AclModel, options?: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
                // Get our selected module details
                const moduleAuth: any = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

                this.apiService.post(L4AclsEnum.POST_HAPROXY_ACL, rule, moduleAuth)
                    .subscribe({
                        next: (data: AclModel): void => {
                            observer.next({ acls: data });
                            observer.complete();
                        },
                        error: (error: ErrorResponseModel): void => {
                            observer.error(error);
                            observer.complete();
                        }
                    });
            }
        );
    }

    /**
     * Update an acl rule via the api
     * @param id
     * @param rule
     * @param options
     */
    public updateAclRule(id: string, rule: AclModel, options?: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
                // Get our selected module details
                const moduleAuth: any = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

                this.apiService.put(L4AclsEnum.PUT_HAPROXY_ACL(id), rule, moduleAuth)
                    .subscribe({
                        next: (response: any): void => {
                            observer.next(response);
                            observer.complete();
                        },
                        error: (error: ErrorResponseModel): void => {
                            observer.error(error);
                            observer.complete();
                        }
                    });
            }
        );
    }

    /**
     * Delete an acl rule via the api
     * @param id
     * @param options
     */
    public deleteAclRule(id: string, options?: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
                // Get our selected module details
                const moduleAuth: any = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

                this.apiService.delete(L4AclsEnum.DELETE_HAPROXY_ACL(id), moduleAuth)
                    .subscribe({
                        next: (response: any): void => {
                            observer.next(response);
                            observer.complete();
                        },
                        error: (error: ErrorResponseModel): void => {
                            observer.error(error);
                            observer.complete();
                        }
                    });
            }
        );
    }

    /**
     * Delete an acl rule via the api
     * @param options
     */
    public hangingAclRules(options?: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
                // Get our selected module details
                const moduleAuth: any = options?.auth || this.authStoreService.getSelectedModuleAuth(ModulesEnum.LOADBALANCER);

                this.apiService.get(L4AclsEnum.GET_HAPROXY_HANGING_ACLS(), moduleAuth)
                    .subscribe({
                        next: (response: any): void => {
                            observer.next(response);
                            observer.complete();
                        },
                        error: (error: ErrorResponseModel): void => {
                            observer.error(error);
                            observer.complete();
                        }
                    });
            }
        );
    }
}
